import { BasicButton, PrimaryButton, TextButton } from '../../../../widgets/buttons';
import { Icon } from '../../../../widgets/icon';
import { PageFooterButtons } from '../../../../widgets/headers';
import { PageHeader } from '../../../../widgets/pageHeader';
import { removeNotification, showErrorNotification, showNotification } from '../../../../widgets/notificationToaster';
import { useDialogManager } from '../../../../widgets/dialog';
import { ContainerBase } from '../../../../widgets/layouts';
import { compressToBase64 } from 'lz-string';
import React, { FC, useState } from 'react';
import { useRefetchableFragment } from 'react-relay';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { useNavigate } from 'react-router-dom';
import { activeSalesChannelFragment } from 'src/components/pages/salesChannels/salesChannel';
import { AccordionItem } from 'src/components/widgets/accordion';
import { Section } from 'src/components/widgets/section';
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { useRelayStore } from 'src/relay';
import config from 'src/shared/config';
import { ProductStatus } from 'src/shared/enums';
import { backofficeUrl, stringFormat, T, toLocalID } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import BsChevronDown from 'src/shared/icons/angle-down-solid.svg';
import BsChevronUp from 'src/shared/icons/angle-up-solid.svg';
import NoImage from 'src/shared/svg/noImage';
import styled from 'styled-components';
import { NoIntegrationDialog } from '../../../../../shared/dialogs';
import { salesChannelsActive$key } from '../../../salesChannels/__generated__/salesChannelsActive.graphql';
import { salesChannelsQuery } from '../../../salesChannels/__generated__/salesChannelsQuery.graphql';
import {
    getAllModelSides,
    loadProductAction,
    setAreasWereDeletedAction,
    toUpdateMutationInput,
    useProductConfigurationChangeStep,
    useProductConfigurationState
} from '../../state';
import { isDefaultColor } from '../../state/model/modelColor';
import { previewPublishedProductsQuery } from './__generated__/previewPublishedProductsQuery.graphql';
import { previewQuery } from './__generated__/previewQuery.graphql';
import {
    ModelColorMappingInput,
    previewSetModelColorMappingSalesChannelsMutation,
    previewSetModelColorMappingSalesChannelsMutation$data
} from './__generated__/previewSetModelColorMappingSalesChannelsMutation.graphql';
import { previewUpdateMutation, previewUpdateMutation$data } from './__generated__/previewUpdateMutation.graphql';

const ProductImageContainer = styled.div`
    width: 100%;
`;

const ProductImage = styled.img`
    width: 100%;
    object-fit: contain;
    border: 1px #ddd solid;
    place-items: center;
    padding: 20px;
    border-radius: 8px;
`;

const _PreviewItem: FC<{ className?: string; name?: string; value?: string }> = (props) => {
    return (
        <div className={props.className}>
            {props.name && <span>{props.name}</span>}
            {props.value && <strong>{props.value}</strong>}
        </div>
    );
};

const PreviewItem = styled(_PreviewItem)`
    padding: 5px 0px;
    //border-bottom: 1px ${(props) => props.theme.app.primary_light} dotted;
    display: flex;
    align-items: center;

    span {
        margin-right: 10px;
    }

    strong {
        font-weight: 600;
    }
`;

const PreviewItemContent = styled.div`
    padding: 20px 0 10px;
`;

const PreviewItemContentProduct = styled.div`
    padding: 10px 0 0;
    font-size: 16px;
`;

const PrewiewCardContentWrapper = styled.div`
    display: grid;
    grid-template-columns: 300px 1fr;
    column-gap: 50px;
`;

const AccordionPreviewSection = styled(AccordionItem)`
    margin: 15px 0;
    border-bottom: 1px ${(props) => props.theme.app.primary_light} solid;
    padding-bottom: 15px;

    svg {
        font-size: 20px;
    }
`;

const SaveTitle = styled.h2`
    text-align: center;
    font-size: 16px;
    font-weight: 400;
    padding: 0 0 35px;
`;

const ProductDetailsContainer = styled.div`
    > div:first-child {
        margin: 10px 0 15px;
    }
`;

const PageHeaderContainer = styled.div`
    margin: 20px 0px !important;

    > div {
        padding-bottom: 0px !important;
    }
`;

const getPublishedProducts = graphql`
    query previewPublishedProductsQuery($productStatusID: Int) {
        models(first: 1, productStatusID: $productStatusID) {
            nodes {
                id
                name
            }
        }
    }
`;

const Preview: FC<{}> = (props) => {
    const invalidate = useRelayStore((state) => state.invalidateRelayStore);
    const { state, dispatch, unsavedChanges } = useProductConfigurationState();
    const changeStep = useProductConfigurationChangeStep();
    const { isAllowed } = usePermissions();
    const { showDialog, closeDialog } = useDialogManager();
    const navigate = useNavigate();
    const { sellerMarketplace } = useSubscriptionData();

    const publishedProducts = useLazyLoadQuery<previewPublishedProductsQuery>(getPublishedProducts, {
        productStatusID: 1
    });

    // Queries
    const query = useLazyLoadQuery<previewQuery>(
        graphql`
            query previewQuery {
                subscription {
                    maxProducts {
                        pricingVersion
                        numProducts
                        numPublishedProducts
                        maxProducts
                    }
                }
                ...salesChannelsActive
            }
        `,
        {}
    );

    const [commitUpdate, isSavingProduct] = useMutation<previewUpdateMutation>(graphql`
        mutation previewUpdateMutation($input: ModelUpdateOrCreateInput, $compressedInput: String) {
            createOrUpdateModelV2(input: $input, compressedInput: $compressedInput) {
                model {
                    ...productFragment_model @relay(mask: false)
                }
            }
        }
    `);

    const [updateModelColorMappingsSalesChannels, isUpdateMappingSalesChannelsInProgress] = useMutation<previewSetModelColorMappingSalesChannelsMutation>(
        graphql`
            mutation previewSetModelColorMappingSalesChannelsMutation($modelID: ID!, $modelColorMappings: [ModelColorMappingInput!]!) {
                setModelColorMapping(modelID: $modelID, modelColorMappings: $modelColorMappings) {
                    colorID
                    merchantCode
                }
            }
        `
    );

    const [saving, setSaving] = useState(false);

    const subscription = query.subscription;
    const [activeSales, refetch] = useRefetchableFragment<salesChannelsQuery, salesChannelsActive$key>(
        activeSalesChannelFragment, // exported graphql`fragment …`
        query
    );
    const hasMasterIntegration = activeSales.salesChannels.filter(
        (channel) => toLocalID(channel.id) == 1 && channel.ecommerceSettings?.ecommerceName !== 'NONE'
    );

    console.log('hasMasterIntegration', hasMasterIntegration);

    const handleSaveClick = async (productStatus?: ProductStatus) => {
        if (!saving) {
            performSave(productStatus);
        }
    };

    const performSave = async (productStatus?: ProductStatus) => {
        setSaving(true);
        const pricingVersion = subscription?.maxProducts.pricingVersion ?? 'VERSION_1';
        const numCurrentPublishedProducts = subscription?.maxProducts.numPublishedProducts ?? 0;
        const numMaxPublishedProducts = subscription?.maxProducts.maxProducts ?? 0;
        const exceedingMaxProductsPublished = numCurrentPublishedProducts >= numMaxPublishedProducts;
        if (pricingVersion === 'VERSION_2' && productStatus) {
            if (!isAllowed(['PRODUCTS_PUBLISH'])) {
                showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
                return;
            } else if (productStatus === ProductStatus.Published && exceedingMaxProductsPublished && hasMasterIntegration) {
                showDialog(
                    'Upgrade_Plan',
                    <UpgradeDialog
                        redirectUrlFromDashboard={encodeURI(window.location.href)}
                        marketPlace={sellerMarketplace ?? undefined}
                        message={
                            <>
                                {stringFormat(
                                    T._(
                                        `Your current plan allows for {0} published products, and you've reached your limit! To publish this product, upgrade your plan to increase your published product slots.`,
                                        'Admin'
                                    ),
                                    numMaxPublishedProducts.toString()
                                )}
                            </>
                        }
                        onUpgradeCompleted={async () => {
                            console.log('upgrade completato, continuo con la pubblicazione');
                            const loadingNotification = showNotification({
                                type: 'default',
                                message: T._('The product is saving...please wait and do not exit the page.', 'Admin')
                            });

                            try {
                                // Save product
                                let mutationInputForUpdateProduct = toUpdateMutationInput(
                                    (exceedingMaxProductsPublished ? state.product.statusID : productStatus) ?? state.product.statusID,
                                    state.salesChannelID,
                                    state.product,
                                    state.sides
                                );

                                console.log({
                                    mutationInputForUpdateProduct
                                });

                                let compressedMutationInput = compressToBase64(JSON.stringify(mutationInputForUpdateProduct));

                                const response = await new Promise<previewUpdateMutation$data>((resolve, reject) =>
                                    commitUpdate({
                                        onCompleted: (response) => {
                                            invalidate();
                                            resolve(response);
                                        },
                                        onError: (response) => reject(response),
                                        updater: (store) => store.invalidateStore(),
                                        variables: config.enableCompressionForSavingReadingProducts
                                            ? {
                                                  compressedInput: compressedMutationInput
                                              }
                                            : {
                                                  input: mutationInputForUpdateProduct
                                              }
                                    })
                                );

                                let modelSaved = response.createOrUpdateModelV2.model!;

                                if (state.product.colorMappings.length > 0 && state.product.additionalCodes.length > 0) {
                                    let colorMappingsForSavedModel = state.product.colorMappings.filter(
                                        (m) => modelSaved.colors.some((c) => c.colorID == m.colorID) || m.productColor
                                    );

                                    // Save color mapping for sales channel
                                    await new Promise<previewSetModelColorMappingSalesChannelsMutation$data>((resolve, reject) =>
                                        updateModelColorMappingsSalesChannels({
                                            variables: {
                                                modelID: state.product.id!,
                                                modelColorMappings: colorMappingsForSavedModel.map((m) => {
                                                    return {
                                                        colorID:
                                                            m.productColor != undefined && m.productColor
                                                                ? (modelSaved?.colors.find((c) => c.code === m.productColor!.code)?.colorID ?? -1)
                                                                : m.colorID,
                                                        salesChannelID: m.salesChannelID,
                                                        merchantCode: m.merchantCode
                                                    } as ModelColorMappingInput;
                                                })
                                            },
                                            onError: (error) => {
                                                showNotification({
                                                    type: 'danger',
                                                    message: <>{T._('An error occurred while mapping product variants. Please try again.', 'Admin')}</>,
                                                    dismiss: {
                                                        duration: 3000
                                                    }
                                                });
                                                reject(error);
                                            },
                                            onCompleted: (response) => {
                                                resolve(response);
                                            }
                                        })
                                    );
                                }

                                if (!state.product.id) {
                                    dispatch(loadProductAction(response!.createOrUpdateModelV2?.model!));
                                }

                                showNotification({
                                    type: 'success',
                                    message: T._('Product successfully saved!', 'Admin'),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });

                                dispatch(setAreasWereDeletedAction(false));

                                // Clear unsaved changes tracking before navigating
                                unsavedChanges.resetSnapshot();
                                changeStep('done');
                                setSaving(false);
                            } catch (ex) {
                                console.error(ex);
                                showNotification({
                                    type: 'danger',
                                    message: T._('Failed saving product. Try again.', 'Admin'),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });
                                setSaving(false);
                            }

                            removeNotification(loadingNotification);
                        }}
                    />
                );

                setSaving(false);
            }
        }
        const loadingNotification = showNotification({
            type: 'default',
            message: T._('The product is saving...please wait and do not exit the page.', 'Admin')
        });

        try {
            //if product is from scratch, even if I choose 'Save and Publish',
            // I have to save it as Unpublished and then try to publish it after integration
            // - if I have no integration, I try to publish it after integrating a store
            // - if I have already a valid integration, I try to connect to model catalog dialog on the 'done' step
            const status = !state.ecommerceSalesChannel.some((e) => e !== 'NONE')
                ? ProductStatus.UnPublished
                : ((exceedingMaxProductsPublished ? state.product.statusID : productStatus) ?? state.product.statusID);

            // Save product
            let mutationInputForUpdateProduct = toUpdateMutationInput(status, state.salesChannelID, state.product, state.sides);

            console.log({
                mutationInputForUpdateProduct
            });

            let compressedMutationInput = compressToBase64(JSON.stringify(mutationInputForUpdateProduct));

            const response = await new Promise<previewUpdateMutation$data>((resolve, reject) =>
                commitUpdate({
                    onCompleted: (response) => {
                        invalidate();
                        resolve(response);
                    },
                    onError: (response) => reject(response),
                    updater: (store) => store.invalidateStore(),
                    variables: config.enableCompressionForSavingReadingProducts
                        ? {
                              compressedInput: compressedMutationInput
                          }
                        : {
                              input: mutationInputForUpdateProduct
                          }
                })
            );

            let modelSaved = response.createOrUpdateModelV2.model!;

            if (state.product.colorMappings.length > 0 && state.product.additionalCodes.length > 0) {
                let colorMappingsForSavedModel = state.product.colorMappings.filter(
                    (m) => modelSaved.colors.some((c) => c.colorID == m.colorID) || m.productColor
                );

                // Save color mapping for sales channel
                await new Promise<previewSetModelColorMappingSalesChannelsMutation$data>((resolve, reject) =>
                    updateModelColorMappingsSalesChannels({
                        variables: {
                            modelID: state.product.id!,
                            modelColorMappings: colorMappingsForSavedModel.map((m) => {
                                return {
                                    colorID:
                                        m.productColor != undefined && m.productColor
                                            ? (modelSaved?.colors.find((c) => c.code === m.productColor!.code)?.colorID ?? -1)
                                            : m.colorID,
                                    salesChannelID: m.salesChannelID,
                                    merchantCode: m.merchantCode
                                } as ModelColorMappingInput;
                            })
                        },
                        onError: (error) => {
                            removeNotification(loadingNotification);
                            showNotification({
                                type: 'danger',
                                message: <>{T._('An error occurred while mapping product variants. Please try again.', 'Admin')}</>,
                                dismiss: {
                                    duration: 3000
                                }
                            });
                            reject(error);
                        },
                        onCompleted: (response) => {
                            resolve(response);
                        }
                    })
                );
            }

            if (!state.product.id) {
                dispatch(loadProductAction(response!.createOrUpdateModelV2?.model!));
            }

            removeNotification(loadingNotification);
            showNotification({
                type: 'success',
                message: T._('Product successfully saved!', 'Admin'),
                dismiss: {
                    duration: 3000
                }
            });

            dispatch(setAreasWereDeletedAction(false));

            if (productStatus === undefined && state.product.statusID === ProductStatus.UnPublished) {
                showNotification({
                    type: 'warning',
                    message: T._('Publish the product to enable store personalization.', 'Admin'),
                    dismiss: {
                        duration: 5000
                    }
                });
            }

            var publishScratch = productStatus != undefined && productStatus === ProductStatus.Published;

            if (publishScratch && !hasMasterIntegration) {
                setSaving(false);

                // Clear unsaved changes tracking - product is already saved
                unsavedChanges.resetSnapshot();
                showDialog(
                    'complete-ecommerce-integration',
                    <NoIntegrationDialog
                        productId={modelSaved.modelID}
                        redirectPage={backofficeUrl(`/product/done?product_id=${modelSaved.modelID}`)}
                        subTitleNoIntegration={T._('Ready to start selling customizable products?', 'Admin')}
                        message={
                            <label>
                                {T._('You need to integrate Zakeke with your store in order to publish and start selling your customizable products.', 'Admin')}
                            </label>
                        }
                    ></NoIntegrationDialog>
                );
                return;
            } else if (productStatus != undefined && productStatus === ProductStatus.Published && status === ProductStatus.UnPublished) {
                setSaving(false);

                const params = new URLSearchParams();
                params.set('product_id', modelSaved.modelID?.toString() ?? '');
                params.set('publish', 'true');

                // Clear unsaved changes tracking before navigating
                unsavedChanges.resetSnapshot();
                navigate(backofficeUrl(`/product/done?${params.toString()}`));
            } else {
                // Clear unsaved changes tracking before navigating
                unsavedChanges.resetSnapshot();
                changeStep('done');
                setSaving(false);
            }
        } catch (ex) {
            console.error(ex);
            showNotification({
                type: 'danger',
                message: T._('Failed saving product. Try again.', 'Admin'),
                dismiss: {
                    duration: 3000
                }
            });
            setSaving(false);
        }

        removeNotification(loadingNotification);
    };

    const getAllModelAreas = () => {
        // Get the model sides because we need the images
        const modelAreas: { colorName: string | null; sideName: string; areaName: string }[] = [];

        for (const modelColor of state.product.colors) {
            for (const modelSide of modelColor.sides) {
                for (const modelAreaIndex in modelSide.areas) {
                    if ((modelSide.areas as Object).hasOwnProperty(modelAreaIndex)) {
                        modelAreas.push({
                            colorName: !isDefaultColor(modelColor) ? modelColor.name : null,
                            sideName: state.sides.find((x) => x.guid == modelSide.originalSideGuid)?.name ?? 'No side',
                            areaName: modelSide.areas[modelAreaIndex].name != '' ? modelSide.areas[modelAreaIndex].name : `Area ${parseInt(modelAreaIndex) + 1}`
                        });
                    }
                }
            }
        }

        return modelAreas;
    };

    return (
        <>
            <ContainerBase>
                <PageHeaderContainer>
                    <PageHeader isBackVisible={false} title={state.product.name} />
                </PageHeaderContainer>
                <Section>
                    {(isAllowed(['PRODUCTS_EDIT']) && <SaveTitle>{T._('Almost ready to publish your product and start selling.', 'Admin')}</SaveTitle>) || (
                        <SaveTitle>{T._('You are seeing the product as read-only', 'Admin')}</SaveTitle>
                    )}
                    <PrewiewCardContentWrapper>
                        <ProductImageContainer>{state.product.imageUrl ? <ProductImage src={state.product.imageUrl} /> : <NoImage />}</ProductImageContainer>
                        <ProductDetailsContainer>
                            <AccordionPreviewSection title={T._('Product', 'Admin')} expandable={false}>
                                <PreviewItemContentProduct>
                                    <PreviewItem name={T._('Name:', 'Admin')} value={state.product.name} />
                                    <PreviewItem name={T._('Code:', 'Admin')} value={state.product.code} />
                                </PreviewItemContentProduct>
                            </AccordionPreviewSection>

                            {!isDefaultColor(state.product.colors[0]) && (
                                <AccordionPreviewSection
                                    title={T._('Variations', 'Admin')}
                                    iconAccordionClosed={
                                        <Icon>
                                            <BsChevronDown />
                                        </Icon>
                                    }
                                    iconAccordionOpened={
                                        <Icon>
                                            <BsChevronUp />
                                        </Icon>
                                    }
                                >
                                    <PreviewItemContent>
                                        {state.product.colors.map((modelColor, index) => {
                                            return <PreviewItem key={index} name={T._('Name:', 'Admin')} value={modelColor.name} />;
                                        })}
                                    </PreviewItemContent>
                                </AccordionPreviewSection>
                            )}

                            <AccordionPreviewSection
                                title={T._('Sides', 'Admin')}
                                iconAccordionClosed={
                                    <Icon>
                                        <BsChevronDown />
                                    </Icon>
                                }
                                iconAccordionOpened={
                                    <Icon>
                                        <BsChevronUp />
                                    </Icon>
                                }
                            >
                                <PreviewItemContent>
                                    {getAllModelSides(state.product).map((modelSide, index) => {
                                        const side = state.sides.find((x) => x.guid == modelSide.originalSideGuid);
                                        return <PreviewItem key={index} name={T._('Name:', 'Admin')} value={side!.name} />;
                                    })}
                                </PreviewItemContent>
                            </AccordionPreviewSection>

                            <AccordionPreviewSection
                                title={T._('Print areas', 'Admin')}
                                iconAccordionClosed={
                                    <Icon>
                                        <BsChevronDown />
                                    </Icon>
                                }
                                iconAccordionOpened={
                                    <Icon>
                                        <BsChevronUp />
                                    </Icon>
                                }
                            >
                                <PreviewItemContent>
                                    {getAllModelAreas().map((area, index) => {
                                        const colorName = area.colorName ? area.colorName + ' -> ' : '';
                                        const sideName = area.sideName + ' -> ';
                                        const areaName = area.areaName;
                                        return <PreviewItem key={index} name={''} value={colorName + sideName + areaName} />;
                                    })}
                                </PreviewItemContent>
                            </AccordionPreviewSection>
                        </ProductDetailsContainer>
                    </PrewiewCardContentWrapper>
                </Section>
            </ContainerBase>
            <PageFooterButtons
                $fixed
                leftContainerContent={isAllowed(['PRODUCTS_EDIT']) && <TextButton onClick={() => changeStep('print-area')}>{T._('Edit', 'Admin')}</TextButton>}
            >
                {(isAllowed(['PRODUCTS_EDIT']) && (
                    <>
                        {state.product.statusID === ProductStatus.UnPublished && (
                            <BasicButton
                                $size='large'
                                disabled={isSavingProduct || saving || isUpdateMappingSalesChannelsInProgress}
                                onClick={() => handleSaveClick(ProductStatus.Published)}
                            >
                                {T._('Save and publish ', 'Admin')}
                            </BasicButton>
                        )}
                        {/*{state.product.statusID === ProductStatus.Published && (*/}
                        {/*	<BasicButton*/}
                        {/*		$size="large"*/}
                        {/*		disabled={isSavingProduct || saving || isUpdateMappingSalesChannelsInProgress}*/}
                        {/*		onClick={() => handleSaveClick(ProductStatus.UnPublished)}*/}
                        {/*	>*/}
                        {/*		{T._('Save and unpublish', 'Admin')}*/}
                        {/*	</BasicButton>*/}
                        {/*)}*/}
                        <PrimaryButton
                            $size='large'
                            disabled={isSavingProduct || saving || isUpdateMappingSalesChannelsInProgress}
                            onClick={() => handleSaveClick()}
                        >
                            {T._('Save', 'Admin')}
                        </PrimaryButton>
                    </>
                )) || (
                    <PrimaryButton $size='large' onClick={() => navigate(backofficeUrl('/products'))}>
                        {T._('Return to the product list', 'Admin')}
                    </PrimaryButton>
                )}
            </PageFooterButtons>
        </>
    );
};

export default Preview;
